import { useCallback } from 'react';
import { Navigation, X } from 'lucide-react';
import { useVenuesStore } from '@/features/venues/store/useVenuesStore';
import { VenueMarker } from '../VenueMarker/index.js';
import { VenueCard } from '../VenueCard/index.js';
import { Stack, Text } from '@/components/ui/index.js';
import styles from './MapView.module.css';

/**
 * MapView - Map display with venue markers
 * 
 * A styled placeholder map with venue markers, current location indicator,
 * recenter button, and selected venue preview card.
 */
export function MapView() {
  const {
    venues,
    selectedVenueId,
    selectVenue,
    clearSelection,
    getSelectedVenue,
  } = useVenuesStore();

  const selectedVenue = getSelectedVenue();

  const handleMarkerClick = useCallback((venue) => {
    selectVenue(venue.id);
  }, [selectVenue]);

  const handleRecenter = useCallback(() => {
    // In a real app, this would recenter the map on user's location
    clearSelection();
  }, [clearSelection]);

  // Generate pseudo-random positions for markers based on venue ID
  // In a real app, these would come from actual coordinates
  const getMarkerPosition = (venue) => {
    const baseTop = 30 + (venue.id * 12) % 50;
    const baseLeft = 20 + (venue.id * 15) % 60;
    return { top: `${baseTop}%`, left: `${baseLeft}%` };
  };

  return (
    <div className={styles.mapContainer}>
      {/* Map Placeholder */}
      <div className={styles.mapPlaceholder}>
        {/* Grid lines for map effect */}
        <div className={styles.gridLines}>
          <div className={styles.gridHorizontal} />
          <div className={styles.gridHorizontal} />
          <div className={styles.gridHorizontal} />
          <div className={styles.gridVertical} />
          <div className={styles.gridVertical} />
          <div className={styles.gridVertical} />
        </div>

        {/* Decorative elements */}
        <div className={styles.mapDecorations}>
          <div className={styles.waterFeature} />
          <div className={styles.parkArea} />
          <div className={styles.road} />
        </div>

        {/* Current Location */}
        <div className={styles.currentLocation}>
          <div className={styles.locationPulse} />
          <div className={styles.locationDot} />
          <span className={styles.locationLabel}>You</span>
        </div>

        {/* Venue Markers */}
        {venues.map(venue => {
          const position = getMarkerPosition(venue);
          return (
            <div
              key={venue.id}
              className={styles.markerWrapper}
              style={position}
            >
              <VenueMarker
                venue={venue}
                isSelected={selectedVenueId === venue.id}
                onClick={() => handleMarkerClick(venue)}
              />
            </div>
          );
        })}
      </div>

      {/* Recenter Button */}
      <button
        className={styles.recenterButton}
        onClick={handleRecenter}
        aria-label="Recenter map"
      >
        <Navigation size={20} />
      </button>

      {/* Selected Venue Preview */}
      {selectedVenue && (
        <div className={styles.previewCard}>
          <button
            className={styles.closePreview}
            onClick={clearSelection}
            aria-label="Close preview"
          >
            <X size={20} />
          </button>
          <VenueCard
            venue={selectedVenue}
            variant="preview"
          />
        </div>
      )}

      {/* Empty selection hint */}
      {!selectedVenue && (
        <div className={styles.selectionHint}>
          <Stack direction="column" gap={1} align="center">
            <Text size="sm" color="secondary">
              Tap a marker to view venue details
            </Text>
          </Stack>
        </div>
      )}
    </div>
  );
}

export default MapView;
